import React, { useState, useEffect } from 'react';
import { EyeIcon, EyeSlashIcon, ArrowLeftIcon, CloseIcon } from '../icons';
import { apiService } from '../../services/apiService';
import { friendlyAuthError } from '../../utils/authErrors';

/**
 * Change an existing password.
 *
 * This dialog used to be decorative: it validated the new password, called an
 * `onPasswordChange` prop that only set React state, and closed — so it
 * reported success while the password on the server was untouched. It now
 * calls POST /api/v1/auth/password/change, which verifies the current password
 * before accepting the new one.
 *
 * Asking for the current password is the point of this screen. An account with
 * no password yet (Google/LinkedIn signup) has nothing to verify against, and
 * takes the "Set a password" path in My Profile instead — if someone reaches
 * this dialog in that state the server says so, and we route them there.
 */

interface ChangePasswordModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Fired after the server confirms the change. */
    onSuccess?: () => void;
}

const PasswordInput = ({ label, placeholder, value, onChange, showPassword, toggleShowPassword, autoComplete, disabled }: { label: string; placeholder: string; value: string; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void; showPassword: boolean; toggleShowPassword: () => void; autoComplete?: string; disabled?: boolean }) => (
    <div className="flex flex-col items-start gap-1.5 flex-1">
        <label className="text-grayscale-1100 font-grotesk text-xs">{label}</label>
        <div className="flex h-11 py-3 px-2.5 items-center gap-2.5 self-stretch rounded-md bg-grayscale-200">
            <input
                type={showPassword ? 'text' : 'password'}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                autoComplete={autoComplete}
                disabled={disabled}
                className="flex-1 bg-transparent text-grayscale-1300 font-grotesk text-sm focus:outline-none placeholder:text-secondary-text disabled:opacity-60"
            />
            <button type="button" onClick={toggleShowPassword} disabled={disabled}>
                {showPassword ? <EyeIcon /> : <EyeSlashIcon />}
            </button>
        </div>
    </div>
);


export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
}) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState('');
    const [serverError, setServerError] = useState('');
    const [isValid, setIsValid] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setError('');
            setServerError('');
            setIsSaving(false);
            setDone(false);
            setShowCurrentPassword(false);
            setShowNewPassword(false);
            setShowConfirmPassword(false);
        }
    }, [isOpen]);

    useEffect(() => {
        const hasUpperCase = /[A-Z]/.test(newPassword);
        const hasNumber = /[0-9]/.test(newPassword);
        const hasSymbol = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(newPassword);
        const isLongEnough = newPassword.length >= 8;
        const passwordsMatch = newPassword !== '' && newPassword === confirmPassword;
        // Catching this here saves a round trip only to be told the obvious.
        const isSameAsCurrent = newPassword !== '' && newPassword === currentPassword;

        if (newPassword && (!isLongEnough || !hasUpperCase || !hasNumber || !hasSymbol)) {
            setError('8+ characters, 1 capital letter, 1 number, and 1 symbol');
        } else if (isSameAsCurrent) {
            setError('Your new password must be different from your current one');
        } else if (newPassword && confirmPassword && !passwordsMatch) {
            setError('Passwords do not match');
        } else {
            setError('');
        }

        setIsValid(
            currentPassword.length > 0 &&
            newPassword.length > 0 &&
            confirmPassword.length > 0 &&
            isLongEnough &&
            hasUpperCase &&
            hasNumber &&
            hasSymbol &&
            passwordsMatch &&
            !isSameAsCurrent
        );

    }, [currentPassword, newPassword, confirmPassword]);

    const handleConfirm = async () => {
        if (!isValid || isSaving) return;
        setServerError('');

        const userId = apiService.getStoredUserId();
        if (!userId) {
            setServerError('Your session has expired. Please log in again.');
            return;
        }

        setIsSaving(true);
        try {
            const res = await apiService.changePassword(userId, currentPassword, newPassword);
            if (res.success) {
                // Hold the dialog open briefly on a confirmation rather than
                // vanishing — closing instantly is what made the old no-op
                // version feel like it had worked.
                setDone(true);
                onSuccess?.();
                setTimeout(() => onClose(), 1200);
            } else if (res.error === 'no_password_set') {
                // The shared copy for this code is written for the login screen
                // ("use the social login above"), which makes no sense here.
                setServerError('This account doesn\u2019t have a password yet. '
                    + 'Open My Profile and use \u201cSet a password\u201d instead.');
            } else {
                setServerError(friendlyAuthError(
                    res.error, 'Could not update your password. Please try again.'));
            }
        } catch {
            setServerError('Network error. Please check your connection and try again.');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 w-full h-full flex justify-center items-center bg-black/70 backdrop-blur-sm z-50 p-4">
            <div className="relative rounded-2xl shadow-lg w-full max-w-[690px] p-6 flex flex-col gap-4 bg-white max-h-[90vh] overflow-y-auto">
                 <div className="absolute top-0 right-0 pt-4 pr-4">
                     <button type="button" onClick={onClose} className="flex p-2.5 justify-center items-center gap-2 rounded-full bg-grayscale-1200 shadow-custom-light">
                       <CloseIcon />
                    </button>
                </div>
                <h2 className="text-grayscale-1300 font-grotesk text-base font-medium">Update Password</h2>

                {done ? (
                    <p className="text-green-700 font-grotesk text-sm py-6">
                        Password updated. You can keep using your existing session — other
                        devices will need the new password next time they sign in.
                    </p>
                ) : (
                <>
                <div className="flex flex-col gap-6">
                    <div className="flex gap-6">
                        <PasswordInput
                            label="Current password"
                            placeholder="Enter your current password"
                            value={currentPassword}
                            onChange={e => setCurrentPassword(e.target.value)}
                            showPassword={showCurrentPassword}
                            toggleShowPassword={() => setShowCurrentPassword(p => !p)}
                            autoComplete="current-password"
                            disabled={isSaving}
                        />
                    </div>
                     <div className="flex flex-col sm:flex-row gap-6">
                        <div className="flex flex-col gap-1.5 flex-1">
                             <PasswordInput
                                label="Enter new password"
                                placeholder="********"
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                showPassword={showNewPassword}
                                toggleShowPassword={() => setShowNewPassword(p => !p)}
                                autoComplete="new-password"
                                disabled={isSaving}
                            />
                            <p className="text-primary-og-600 font-grotesk text-xs">8+ characters, 1 capital letter, 1 number, and 1 symbol</p>
                        </div>
                        <div className="flex flex-col gap-1.5 flex-1">
                             <PasswordInput
                                label="Confirm new password"
                                placeholder="Re-enter new password"
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                showPassword={showConfirmPassword}
                                toggleShowPassword={() => setShowConfirmPassword(p => !p)}
                                autoComplete="new-password"
                                disabled={isSaving}
                            />
                        </div>
                    </div>
                </div>

                {/* The rule hint above covers the "too weak" case, so only show a
                    validation line for the problems it does not describe. */}
                {error && error !== '8+ characters, 1 capital letter, 1 number, and 1 symbol' && (
                    <p className="text-red-500 font-grotesk text-xs">{error}</p>
                )}
                {serverError && (
                    <p className="text-red-500 font-grotesk text-sm">{serverError}</p>
                )}

                <div className="flex items-center gap-3 self-stretch mt-4">
                    <button type="button" onClick={onClose} disabled={isSaving} className="flex py-2.5 px-4 justify-center items-center gap-2 flex-1 rounded-full border border-primary-og-600 bg-white shadow-custom-light disabled:opacity-50">
                        <ArrowLeftIcon />
                        <span className="text-grayscale-1300 text-center font-grotesk text-sm font-medium">Back</span>
                    </button>
                     <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={!isValid || isSaving}
                        className={`flex py-2.5 px-4 justify-center items-center gap-2 flex-1 rounded-full shadow-custom-light transition-colors ${isValid && !isSaving ? 'bg-primary-og-600 text-white' : 'bg-primary-100 text-primary-300'}`}
                    >
                        <span className="text-center font-grotesk text-sm font-medium">
                            {isSaving ? 'Updating…' : 'Confirm'}
                        </span>
                        {!isSaving && (
                         <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4 8H12" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M8 12V4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        )}
                    </button>
                </div>
                </>
                )}
            </div>
        </div>
    );
};
